import { BaseWebComponentView } from '../../../../../../../../framework/BaseWebComponentView';
import { GridViewWebComponent } from '../../../../../../../../grid/gridView';
import { WEB_COMPONENT_GRID_VIEW } from '../../../../../../../../webcomponents.constants';
import { ColumnDetails, ColumnType } from '../../../../../../../../grid/types/common';
import { translateFn } from '../../../../../../../../utils/translator.helper';
import { GridTitleConfig } from '../../../../../../../../grid/uiElements/gridViewTitle/types';
import { PatchesGridDataProvider } from '../dataProvider/PatchesGridDataProvider';
import { PatchesColumnIndexes, PatchesColumnKeys, PatchesGridItem } from '../types';
import { categoryRenderer, numericRenderer } from './customGridRenderers';
import { TaskTarget } from '../../../../../../types';

export class PatchesGridView extends BaseWebComponentView {
  public targets: Array<TaskTarget>;

  public dom: {
    $gridView: GridViewWebComponent<PatchesColumnIndexes, PatchesColumnKeys>;
  };

  getDomElements(): void {
    this.dom.$gridView = this.shadow.querySelector(WEB_COMPONENT_GRID_VIEW);

    if (this.dom.$gridView.injectDependencies) {
      this.dom.$gridView.injectDependencies(this.dependencies);
      this.dom.$gridView.start().then().catch(console.error);
    }
  }

  buildView(): void {
    this.buildGridView();
  }

  private buildGridView() {
    const columnDetails: Array<ColumnDetails<PatchesColumnIndexes, PatchesColumnKeys>> = [
      {
        key: PatchesColumnKeys.PATCH_NAME,
        column: PatchesColumnIndexes.PATCH_NAME,
        columnName: translateFn('Patch name'),
        type: ColumnType.STRING,
        removable: false,
        sortable: true,
      },
      {
        key: PatchesColumnKeys.CVE_COUNT,
        column: PatchesColumnIndexes.CVE_COUNT,
        columnName: translateFn('CVE'),
        type: ColumnType.STRING,
        removable: false,
        filteringDisabled: true,
        sortable: true,
      },
      {
        key: PatchesColumnKeys.CATEGORY,
        column: PatchesColumnIndexes.CATEGORY,
        columnName: translateFn('Category'),
        type: ColumnType.SELECT,
        removable: false,
        sortable: true,
        values: [
          {
            label: translateFn('Security'),
            value: Constants.PatchManagement.PatchTypes.SECURITY_PATCH,
          },
          {
            label: translateFn('Non-security'),
            value: Constants.PatchManagement.PatchTypes.NON_SECURITY_PATCH,
          },
          {
            label: translateFn('Manually approved'),
            value: Constants.PatchManagement.PatchTypes.MANUALLY_APPROVED,
          },
        ],
      },
      {
        key: PatchesColumnKeys.PRODUCTS_COUNT,
        column: PatchesColumnIndexes.PRODUCTS_COUNT,
        columnName: translateFn('Products'),
        type: ColumnType.STRING,
        removable: false,
        filteringDisabled: true,
        sortable: true,
      },
    ];

    this.dom.$gridView.propertiesSet = false;

    this.dom.$gridView.columnRenderers = {
      [PatchesColumnKeys.CATEGORY]: categoryRenderer,
      [PatchesColumnKeys.CVE_COUNT]: numericRenderer,
      [PatchesColumnKeys.PRODUCTS_COUNT]: numericRenderer,
    };

    this.dom.$gridView.dataProvider = new PatchesGridDataProvider(this.dependencies);
    (this.dom.$gridView.dataProvider as PatchesGridDataProvider).targets = this.targets;
    this.dom.$gridView.hideHeaderActions = true;
    this.dom.$gridView.hideFiltersToggle = true;
    this.dom.$gridView.hideResetFilters = true;
    this.dom.$gridView.viewId = 'install-patches-grid';
    this.dom.$gridView.defaultFlexGrowColumn = PatchesColumnKeys.PRODUCTS_COUNT;
    this.dom.$gridView.columns = [
      PatchesColumnIndexes.PATCH_NAME,
      PatchesColumnIndexes.CVE_COUNT,
      PatchesColumnIndexes.CATEGORY,
      PatchesColumnIndexes.PRODUCTS_COUNT,
    ];
    this.dom.$gridView.columnConfigs = [
      {
        key: PatchesColumnKeys.PATCH_NAME,
        width: '300px',
      },
      {
        key: PatchesColumnKeys.CVE_COUNT,
        width: '80px',
      },
      {
        key: PatchesColumnKeys.CATEGORY,
        width: '104px',
      },
      {
        key: PatchesColumnKeys.PRODUCTS_COUNT,
        width: '84px',
      },
    ];
    this.dom.$gridView.predefinedFiltersKeys = [PatchesColumnKeys.PATCH_NAME, PatchesColumnKeys.CATEGORY];
    this.dom.$gridView.filters = [
      {
        [PatchesColumnKeys.PATCH_NAME]: { contains: '' },
      },
      {
        [PatchesColumnKeys.CATEGORY]: { value: '' },
      },
    ];
    this.dom.$gridView.filtersUiConfigs = [
      {
        key: PatchesColumnKeys.CATEGORY,
        config: {
          isClearable: true,
        },
      },
    ];
    this.dom.$gridView.allColumnDetails = columnDetails;
    this.dom.$gridView.hasSelectableRows = true;
    this.dom.$gridView.hasSelectionExtraOptions = true;
    this.dom.$gridView.isCompactViewChecked = false;
    this.dom.$gridView.titleConfig = new GridTitleConfig(translateFn('Patches'));
    this.dom.$gridView.showToolbarRefresh = false;
    this.dom.$gridView.hasPagination = true;
    this.dom.$gridView.pageSize = 20;
    this.dom.$gridView.enableRowClickEvent = true;
    this.dom.$gridView.shouldLoadAsync = true;
    this.dom.$gridView.propertiesSet = true;
  }

  public getSelectedGridItems(): Array<PatchesGridItem> {
    return this.dom.$gridView.selectedItems as Array<PatchesGridItem>;
  }
}
